/**
 * A edição do CPTu — o que o engenheiro corrige SEM tocar no bruto
 * (spec 07 §5.4; RN-252, RN-253).
 *
 * As leituras do equipamento ficam como vieram (`cptu_leitura` só acrescenta).
 * A edição é uma premissa da vertical, guardada e auditada à parte, e o motor a
 * aplica ANTES das contas, sobre uma cópia: `aplicarEdicao` nunca altera o
 * `Leituras` que recebe e só devolve séries novas.
 *
 * A ordem é sempre a mesma, e é ela que dá o sentido a cada passo:
 *
 *   1. cortes ....... tiram as leituras com `de ≤ z ≤ ate` (pré-furo, trecho perdido);
 *   2. picos ........ tiram a leitura cujo canal foge da mediana da janela;
 *   3. deslocamento . faz fs e u₂ valerem, em z, o que valiam em `z − d/100`
 *                     (a ponta do cone lê qc, fs e u₂ em alturas diferentes);
 *   4. filtros ...... média móvel centrada, por canal.
 *
 * Cortes e picos tiram a LEITURA inteira (todos os canais saem juntos e a série
 * continua alinhada); cada uma fica listada em `removidas`, com o índice na
 * série original, a profundidade e o motivo — a edição é visível, não some com
 * dado. «Não se aplica» é NaN (nunca 0): é o que sobra nas pontas do
 * deslocamento, onde não há de onde tirar o valor.
 *
 * A profundidade é suposta crescente (é como o cone desce).
 */
import type { Leituras, Serie } from './tipos';

/** Os canais que a edição mexe; profundidade e os canais opcionais só são alinhados. */
export type CanalDaEdicao = 'qc' | 'fs' | 'u2';

/** O que o engenheiro editou numa vertical; fica em `cptu_vertical.edicao`. */
export interface Edicao {
  /** Trechos tirados, `de ≤ z ≤ ate` (m), com o motivo que vai para a lista de removidas. */
  cortes: { de: number; ate: number; motivo: string }[];
  /**
   * Remoção de picos: `janela` leituras (centrada) e `limite` desvios absolutos
   * da mediana (MAD), nos canais listados; `null` = não remove.
   */
  picos: { janela: number; limite: number; canais: CanalDaEdicao[] } | null;
  /** Deslocamento em cm; positivo = o canal desce (passa a valer o que valia mais acima). */
  deslocamento: { fs: number; u2: number };
  /** Janela da média móvel, em leituras; 1 = sem filtro. */
  filtros: { qc: number; fs: number; u2: number };
}

/** Uma leitura que a edição tirou: o índice na série ORIGINAL, a profundidade e o porquê. */
export interface LeituraRemovida {
  ordem: number;
  z: number;
  motivo: string;
}

/** Sem ajuste nenhum. Congelada: quem precisa de uma edição para alterar copia-a. */
export const EDICAO_VAZIA: Edicao = Object.freeze({
  cortes: Object.freeze([] as Edicao['cortes']) as Edicao['cortes'],
  picos: null,
  deslocamento: Object.freeze({ fs: 0, u2: 0 }),
  filtros: Object.freeze({ qc: 1, fs: 1, u2: 1 }),
});

/** Folga nas comparações de profundidade: 1 nm, só para o ruído de ponto flutuante (0,06 − 0,05 ≠ 0,01). */
const TOL_Z = 1e-9;

/** Menos de 3 valores na janela não dão mediana nem MAD que sirvam para chamar algo de pico. */
const MIN_NA_JANELA = 3;

/** A ordem fixa dos canais, para o motivo de uma leitura que é pico em mais de um sair sempre igual. */
const CANAIS: CanalDaEdicao[] = ['qc', 'fs', 'u2'];

const numeroOu = (v: unknown, padrao: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : padrao;

/**
 * A edição como o motor a lê: o que falta (um JSON antigo do banco, uma edição
 * só com cortes) vale «sem ajuste», e o que não é número vale o neutro.
 */
function normalizar(e: Edicao | null | undefined): Edicao {
  const bruta: Partial<Edicao> = e ?? {};
  return {
    cortes: Array.isArray(bruta.cortes) ? bruta.cortes : [],
    picos: bruta.picos ?? null,
    deslocamento: {
      fs: numeroOu(bruta.deslocamento?.fs, 0),
      u2: numeroOu(bruta.deslocamento?.u2, 0),
    },
    filtros: {
      qc: numeroOu(bruta.filtros?.qc, 1),
      fs: numeroOu(bruta.filtros?.fs, 1),
      u2: numeroOu(bruta.filtros?.u2, 1),
    },
  };
}

/** A mediana dos `m` primeiros valores do buffer (que fica ordenado). */
function mediana(buffer: Float64Array, m: number): number {
  const v = buffer.subarray(0, m);
  v.sort();
  return m % 2 === 1 ? v[(m - 1) / 2] : (v[m / 2 - 1] + v[m / 2]) / 2;
}

/** `s` nas posições `indices`, em série nova. */
function escolher(s: Serie, indices: number[]): Serie {
  const saida = new Float64Array(indices.length);
  for (let k = 0; k < indices.length; k++) saida[k] = s[indices[k]];
  return saida;
}

/**
 * Média móvel centrada de `janela` leituras (janela ímpar; uma janela par vale a
 * ímpar seguinte, para continuar centrada). Nas pontas a janela encolhe — não
 * se inventa leitura além da série. O NaN não entra na média, e a leitura que
 * é NaN continua NaN (o filtro suaviza dado, não cria dado). Janela 1 (ou
 * menor) devolve uma cópia sem filtro; a série de entrada nunca muda.
 */
export function mediaMovel(s: Serie, janela: number): Serie {
  const n = s.length;
  const saida = new Float64Array(n);
  const meia = Number.isFinite(janela) && janela > 1 ? Math.floor(janela / 2) : 0;
  for (let i = 0; i < n; i++) {
    if (meia === 0 || Number.isNaN(s[i])) {
      saida[i] = s[i];
      continue;
    }
    let soma = 0;
    let validos = 0;
    const fim = Math.min(n - 1, i + meia);
    for (let j = Math.max(0, i - meia); j <= fim; j++) {
      const v = s[j];
      if (Number.isNaN(v)) continue;
      soma += v;
      validos++;
    }
    saida[i] = soma / validos; // o centro é válido: validos ≥ 1
  }
  return saida;
}

/**
 * Quais leituras da série são pico: a distância à mediana da janela (centrada,
 * `janela` leituras, encolhendo nas pontas) passa de `limite` × MAD da janela,
 * o desvio absoluto mediano — sem o fator 1,4826 de «sigma», porque o plano
 * pede o limite em desvios absolutos da mediana. O NaN não entra na janela e
 * nunca é pico; janela com menos de 3 valores não decide nada.
 *
 * Janela com MAD = 0 (mais da metade dos valores iguais à mediana: uma série
 * constante, o fs = 0 colado à superfície, ou o qc em passos de 0,01 MPa, que
 * repete o mesmo valor com frequência): o MAD não mede mais nada, e dividir por
 * ele acusaria qualquer diferença mínima. Nesse caso a escala passa a ser o
 * desvio absoluto MÉDIO em torno da mediana (sem fator de escala), que continua
 * zero só se a janela inteira é igual. Nas dez verticais do 2609350 (janela 11,
 * limite 5) o qc tem MAD = 0 em 24 % das janelas: com um piso minúsculo no lugar
 * do desvio médio, 8 % das leituras de qc seriam chamadas de pico; assim são
 * 0,8 %. É o que ainda tira o 40,0 solto numa série de 2,0 (o desvio médio é
 * 38/11 e 38 passa de 5 × 3,45); um pico isolado só passa do limite quando a
 * janela tem mais leituras que o limite. Um degrau real (2,0 → 8,0 que fica)
 * não perde nada: a mediana da janela de cada leitura já acompanha o lado do
 * degrau em que ela está.
 */
function detectarPicos(s: Serie, janela: number, limite: number): Uint8Array {
  const n = s.length;
  const picos = new Uint8Array(n);
  if (!(Number.isFinite(janela) && Number.isFinite(limite) && limite > 0)) return picos;
  // Uma janela maior que a série vale a série inteira: a meia janela não passa de n (sem isso, uma
  // janela de 10¹⁰ digitada por engano pediria um buffer de 10¹⁰ posições).
  const meia = Math.min(Math.floor(janela / 2), n);
  if (meia < 1) return picos;
  const valores = new Float64Array(2 * meia + 1);
  const desvios = new Float64Array(2 * meia + 1);
  for (let i = 0; i < n; i++) {
    const x = s[i];
    if (Number.isNaN(x)) continue;
    let m = 0;
    const fim = Math.min(n - 1, i + meia);
    for (let j = Math.max(0, i - meia); j <= fim; j++) {
      if (!Number.isNaN(s[j])) valores[m++] = s[j];
    }
    if (m < MIN_NA_JANELA) continue;
    const med = mediana(valores, m);
    for (let k = 0; k < m; k++) desvios[k] = Math.abs(valores[k] - med);
    let escala = mediana(desvios, m); // o MAD
    if (!(escala > 0)) {
      escala = 0;
      for (let k = 0; k < m; k++) escala += desvios[k];
      escala /= m; // MAD = 0: o desvio absoluto médio
    }
    if (Math.abs(x - med) > limite * escala) picos[i] = 1;
  }
  return picos;
}

/**
 * O canal passa a valer, na profundidade z, o que valia em `z − cm/100`
 * (interpolação linear sobre a série; positivo = o canal desce). Onde esse
 * ponto cai fora da série, ou entre duas leituras das quais uma é NaN, o
 * resultado é NaN.
 */
function deslocar(z: Serie, s: Serie, cm: number): Serie {
  if (cm === 0) return s;
  const n = z.length;
  const saida = new Float64Array(n);
  const d = cm / 100;
  for (let i = 0; i < n; i++) {
    const alvo = z[i] - d;
    // j = a última leitura com z ≤ alvo (busca binária; z é crescente).
    let lo = 0;
    let hi = n;
    while (lo < hi) {
      const meio = (lo + hi) >>> 1;
      if (z[meio] <= alvo) lo = meio + 1;
      else hi = meio;
    }
    const j = lo - 1;
    if (j < 0) {
      saida[i] = z[0] - alvo <= TOL_Z ? s[0] : NaN;
    } else if (j === n - 1) {
      saida[i] = alvo - z[n - 1] <= TOL_Z ? s[n - 1] : NaN;
    } else {
      const f = (alvo - z[j]) / (z[j + 1] - z[j]); // z[j+1] > alvo ≥ z[j]: o divisor é positivo
      saida[i] = f === 0 ? s[j] : s[j] + (s[j + 1] - s[j]) * f;
    }
  }
  return saida;
}

/**
 * Aplica a edição às leituras: cortes, picos, deslocamento e filtros, nessa
 * ordem. Devolve cópias — o `Leituras` de entrada nunca muda — e a lista das
 * leituras removidas (uma por leitura, em ordem crescente de `ordem`, o índice
 * na série original). Sem edição (`null`, `undefined` ou `EDICAO_VAZIA`) a
 * saída é uma cópia idêntica e nada é removido.
 *
 * Um pico dentro de um corte conta como corte (o corte vem antes). A leitura
 * que é pico em mais de um canal sai uma vez só, com o motivo
 * `pico em qc, fs e u2` (na ordem qc, fs, u2).
 */
export function aplicarEdicao(
  l: Leituras,
  e: Edicao | null | undefined,
): { leituras: Leituras; removidas: LeituraRemovida[] } {
  const ed = normalizar(e);
  const n = l.profundidade.length;
  const motivo: (string | null)[] = new Array<string | null>(n).fill(null);

  // 1. cortes: de ≤ z ≤ ate (os dois extremos entram); o primeiro corte da lista que pegar dá o motivo.
  for (const corte of ed.cortes) {
    if (!corte) continue; // um nulo perdido na lista do banco não derruba a vertical
    const rotulo = corte.motivo || 'corte';
    for (let i = 0; i < n; i++) {
      const z = l.profundidade[i];
      if (motivo[i] === null && z >= corte.de - TOL_Z && z <= corte.ate + TOL_Z) motivo[i] = rotulo;
    }
  }

  // 2. picos: só nas leituras que sobraram do corte, canal a canal, cada canal sobre a série de todos.
  if (ed.picos) {
    const vivas: number[] = [];
    for (let i = 0; i < n; i++) if (motivo[i] === null) vivas.push(i);
    const canais = CANAIS.filter((c) => ed.picos?.canais?.includes(c));
    const em: CanalDaEdicao[][] = vivas.map(() => []);
    for (const canal of canais) {
      const marcas = detectarPicos(escolher(l[canal], vivas), ed.picos.janela, ed.picos.limite);
      marcas.forEach((marca, k) => {
        if (marca) em[k].push(canal);
      });
    }
    em.forEach((lista, k) => {
      if (lista.length === 0) return;
      const nomes = lista.length === 1 ? lista[0] : `${lista.slice(0, -1).join(', ')} e ${lista[lista.length - 1]}`;
      motivo[vivas[k]] = `pico em ${nomes}`;
    });
  }

  const mantidas: number[] = [];
  const removidas: LeituraRemovida[] = [];
  for (let i = 0; i < n; i++) {
    const porque = motivo[i];
    if (porque === null) mantidas.push(i);
    else removidas.push({ ordem: i, z: l.profundidade[i], motivo: porque });
  }

  // Cópias, já sem as removidas. Os canais opcionais só andam alinhados.
  const profundidade = escolher(l.profundidade, mantidas);
  let qc = escolher(l.qc, mantidas);
  let fs = escolher(l.fs, mantidas);
  let u2 = escolher(l.u2, mantidas);

  // 3. deslocamento (sobre o que sobrou dos cortes e picos).
  fs = deslocar(profundidade, fs, ed.deslocamento.fs);
  u2 = deslocar(profundidade, u2, ed.deslocamento.u2);

  // 4. filtros, depois de deslocar. Janela 1 devolve a série como está (já é cópia).
  if (ed.filtros.qc > 1) qc = mediaMovel(qc, ed.filtros.qc);
  if (ed.filtros.fs > 1) fs = mediaMovel(fs, ed.filtros.fs);
  if (ed.filtros.u2 > 1) u2 = mediaMovel(u2, ed.filtros.u2);

  const leituras: Leituras = { profundidade, qc, fs, u2 };
  if (l.inclinacao) leituras.inclinacao = escolher(l.inclinacao, mantidas);
  if (l.velocidade) leituras.velocidade = escolher(l.velocidade, mantidas);
  if (l.tempo) leituras.tempo = escolher(l.tempo, mantidas);
  return { leituras, removidas };
}
